import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2, Trophy, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { PetAvatar } from "@/components/PetAvatar";
import { UserLink } from "@/components/UserBits";
import { PetGallery } from "@/components/PetGallery";

function PetTile({ p, rank, onLike }) {
  return (
    <div className={`relative rounded-3xl bg-white border p-4 text-center fade-up ${rank === 0 && p.likes > 0 ? "border-amber-300 shadow-lg shadow-amber-100" : "border-pink-100"}`} data-testid={`playground-pet-${p.owner.user_id}`}>
      {rank < 3 && p.likes > 0 && <span className="absolute top-3 left-3 w-7 h-7 rounded-full vip-gold-metal text-xs font-extrabold flex items-center justify-center">{rank + 1}</span>}
      <PetAvatar kind={p.kind} name={p.name} equipped={p.equipped} className="w-28 h-28 sm:w-32 sm:h-32 mx-auto" />
      <p className="font-display font-extrabold mt-2 truncate">{p.name}</p>
      <p className="text-xs text-slate-500 truncate">von <UserLink id={p.owner.user_id}>{p.owner.name}</UserLink></p>
      {p.wins > 0 && <p className="text-[10px] font-extrabold text-amber-600 mt-1 flex items-center justify-center gap-1" data-testid={`playground-wins-${p.owner.user_id}`}><Trophy className="w-3 h-3" /> {p.wins}× Outfit der Woche</p>}
      <button onClick={() => onLike(p)} disabled={p.me} data-testid={`playground-like-${p.owner.user_id}`} aria-pressed={p.liked}
        className={`mt-3 h-10 px-4 rounded-full text-sm font-bold inline-flex items-center gap-1.5 transition-[transform,background-color] active:scale-90 disabled:opacity-60 ${p.liked ? "pink-gradient text-white" : "bg-rose-50 text-rose-600 hover:bg-rose-100"}`}>
        <Heart className={`w-4 h-4 ${p.liked ? "fill-white" : ""}`} /> <span data-testid={`playground-likes-${p.owner.user_id}`}>{p.likes}</span>
      </button>
    </div>
  );
}

export default function Playground() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/pets/playground").then(({ data }) => setD(data)).catch((e) => { toast.error(formatErr(e)); setD({ pets: [], prize: 300 }); }); }, []);
  const like = async (p) => {
    try {
      const { data } = await api.post(`/pets/${p.owner.user_id}/like`);
      setD((x) => ({ ...x, pets: x.pets.map((q) => (q.owner.user_id === p.owner.user_id ? { ...q, liked: data.liked, likes: data.likes, total_likes: Math.max(0, (q.total_likes || 0) + (data.liked ? 1 : -1)) } : q)) }));
    } catch (e) { toast.error(formatErr(e)); }
  };
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="playground-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-wider uppercase font-semibold text-rose-600">Haustier-Spielplatz</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Schönstes Outfit der Woche</h1>
          <p className="text-sm text-slate-500 mt-1">Verteile Herzen an deine Lieblings-Outfits. Das Haustier mit den meisten Herzen bis Sonntag 20 Uhr gewinnt <b>{d.prize} Punkte</b>.</p>
        </div>
        <Link to="/haustier" data-testid="playground-my-pet" className="h-11 px-5 rounded-full pink-gradient text-white font-bold text-sm inline-flex items-center gap-2"><PawPrint className="w-4 h-4" /> Mein Haustier stylen</Link>
      </div>
      {d.last?.winner && (
        <div className="rounded-3xl bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200 p-4 flex items-center gap-4" data-testid="playground-last-winner">
          {d.last.pet && <PetAvatar kind={d.last.pet.kind} name={d.last.pet.name} equipped={d.last.pet.equipped} className="w-16 h-16 shrink-0" />}
          <p className="text-sm"><Trophy className="w-4 h-4 inline text-amber-500" /> Letzte Woche gewann <b>{d.last.pet?.name}</b> von <b>{d.last.winner.name}</b> mit {d.last.likes} Herzen.</p>
        </div>
      )}
      {d.pets.length < 3 && <p className="text-sm text-center text-slate-500 bg-rose-50/60 rounded-2xl p-4" data-testid="playground-few">Der Spielplatz füllt sich gerade erst – adoptiere ein Haustier, style es und hol dir die ersten Herzen!</p>}
      {d.pets.length === 0 ? null : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="playground-grid">
          {d.pets.slice(0, 8).map((p, i) => <PetTile key={p.owner.user_id} p={p} rank={i} onLike={like} />)}
        </div>
      )}
      {d.pets.length > 0 && <PetGallery pets={d.pets} onLike={like} />}
    </div>
  );
}
